// pms-core / domain — « Administration fiscale » (P3 F4c-2, brief §11.14 point 7, §11.17–11.19).
//
// What the screen reads and the rules it needs, pure. The reads and the download are an adapter's; this file decides
// only which archive files may be served and what the downloaded folder is called.

/** One caisse as the administration sees it. */
export interface FiscalAuditCaisse {
  id: number;
  code: string;
  trainingOnly: boolean;
  openedAt: string;
  closuresFrom: string | null;
  lastSequence: number;
  chainHead: string;
}

/** A key of the register: the record seal (`mac-*`, its secret in Vault) or a clôture-head signing key (`sig-*`). */
export interface FiscalAuditKey {
  keyId: string;
  purpose: string;
  algorithm: string;
  publicKey: string | null;
  validFrom: string;
  validTo: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
}

/** OpenTimestamps: none yet, pending on the calendars, or completed in a Bitcoin block. */
export type OtsState = 'pending' | 'bitcoin' | null;

export interface FiscalAuditClosure {
  id: number;
  caisseId: number;
  kind: 'daily' | 'monthly' | 'fiscal_year';
  periodStart: string;
  periodEnd: string;
  isTraining: boolean;
  documentCount: number;
  paymentCount: number;
  vatBreakdown: Array<{ rate: string | null; ttc_cents: string; ht_cents: string; vat_cents: string }>;
  tenderTakings: Array<{ tender: string | null; movement: string; count: string; amount_cents: string }>;
  periodTotalCents: number;
  grandTotalCents: number;
  perpetualTotalCents: number;
  throughSequence: number;
  closedAt: string;
  /** The signing key, or null while unsigned. */
  keyId: string | null;
  tsa: boolean;
  ots: OtsState;
}

export interface FiscalAuditArchive {
  id: number;
  caisseId: number;
  kind: 'monthly' | 'fiscal_year' | 'on_demand';
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
  requestId: number | null;
  requestedBy: string | null;
  manifestSha256: string;
  softwareVersion: string;
  /** Signed, witnessed and its seal file written: complete, downloadable. */
  sealed: boolean;
  ots: OtsState;
}

export interface FiscalAuditRequest {
  id: number;
  caisseId: number;
  periodStart: string;
  periodEnd: string;
  requestedAt: string;
  requestedBy: string | null;
  reason: string | null;
  archiveId: number | null;
  lastError: string | null;
}

export interface FiscalAuditVerification {
  caisseId: number;
  occurredAt: string;
  by: string | null;
  /** `sql` (« Vérifier l'intégrité ») or `verifier` (the independent program, reported). */
  method: string;
  ok: string | null;
  lastSequence: string | null;
  findings: string | null;
  summary: string | null;
}

export interface FiscalAuditOverview {
  version: string;
  caisses: FiscalAuditCaisse[];
  keys: FiscalAuditKey[];
  closures: FiscalAuditClosure[];
  archives: FiscalAuditArchive[];
  requests: FiscalAuditRequest[];
  verifications: FiscalAuditVerification[];
}

/** A « Vérifier l'intégrité » run as the server holds it. `ok` is null until `done`. */
export interface FiscalVerificationRun {
  runId: number;
  caisseId: number;
  targetSequence: number;
  checkedThrough: number;
  links: number;
  documentsV1: number;
  closures: number;
  closuresNotYetSealed: number;
  findingCount: number;
  findings: string[];
  done: boolean;
  ok: boolean | null;
  eventId: number | null;
}

/**
 * The files an archive may serve: its manifest, seal, export, notice and the verifier's own files — nothing else,
 * and never a path that climbs out of the archive's prefix.
 */
export function isArchiveFilePath(path: string): boolean {
  if (path.includes('..') || path.includes('//') || path.startsWith('/')) return false;
  return /^(manifest\.json|seal\.json|export\.json|NOTICE\.fr\.md|verifier\/[A-Za-z0-9_.-]+)$/.test(path);
}

/** The downloaded folder: `archive-fiscale-<caisse>-<kind>-<period>` — what an inspector files it under. */
export function archiveFolderName(caisseCode: string, a: Pick<FiscalAuditArchive, 'kind' | 'periodStart' | 'periodEnd'>): string {
  const period =
    a.kind === 'monthly' ? a.periodStart.slice(0, 7)
    : a.kind === 'fiscal_year' ? `${a.periodStart.slice(0, 7)}_${a.periodEnd.slice(0, 7)}`
    : `${a.periodStart}_${a.periodEnd}`;
  const kind = a.kind === 'monthly' ? 'mois' : a.kind === 'fiscal_year' ? 'exercice' : 'demande';
  return `archive-fiscale-${caisseCode}-${kind}-${period}`;
}

// ── Inspector access (F4c-3, brief §11.17 Q10/Q12, §11.20) ───────────────────

/** An inspector's access as the register holds it. */
export interface FiscalInspector {
  id: number;
  propertyCode: string;
  email: string;
  fullName: string;
  authority: string | null;
  grantedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  withdrawnAt: string | null;
}

/** An instant from either form the stack writes: ISO (`…T…+00:00`) or Postgres text (`… …`, UTC when unzoned). */
function instant(s: string): number {
  const t = s.replace(' ', 'T');
  return Date.parse(/(Z|[+-]\d{2}(:?\d{2})?)$/.test(t) ? t : `${t}Z`);
}

/** Open while not withdrawn and not past its term (the hourly sweep withdraws it within the hour). */
export function inspectorState(
  i: Pick<FiscalInspector, 'expiresAt' | 'revokedAt' | 'withdrawnAt'>,
  now: Date,
): 'open' | 'expired' | 'revoked' {
  if (i.revokedAt) return 'revoked';
  if (i.withdrawnAt || instant(i.expiresAt) <= now.getTime()) return 'expired';
  return 'open';
}

export interface InspectorAccessInput {
  propertyCode: string;
  email: string;
  fullName: string;
  authority: string | null;
  days: number;
}

/** What opening an access returns — the password ONCE; it is stored nowhere but in the auth service's hash. */
export interface InspectorAccessOpened {
  inspectorId: number;
  email: string;
  password: string;
  expiresAt: string;
}

/** A plausible e-mail, lower-cased and trimmed — or null. */
export function normaliseInspectorEmail(raw: string): string | null {
  const e = raw.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? e : null;
}

const PASSWORD_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789'; // no i l o 0 1: read aloud or copied by hand

/**
 * A temporary password from 20 random bytes (Q16: shown once, handed over in person): four groups of five from a
 * 31-letter alphabet, ~99 bits. Rejection sampling, so every letter is equally likely.
 */
export function inspectorPassword(randomBytes: (n: number) => Uint8Array): string {
  const out: string[] = [];
  const limit = 256 - (256 % PASSWORD_ALPHABET.length);
  for (let round = 0; out.length < 20; round++) {
    // ~3 % of bytes are rejected: a source still short after 16 rounds is broken, not unlucky — fail, never spin
    if (round >= 16) throw new Error('inspectorPassword: the random source gives no usable bytes');
    for (const b of randomBytes(32)) {
      if (b < limit && out.length < 20) out.push(PASSWORD_ALPHABET[b % PASSWORD_ALPHABET.length]!);
    }
  }
  return [0, 5, 10, 15].map((i) => out.slice(i, i + 5).join('')).join('-');
}
